/**
 * Global theme script, loaded deferred on every page.
 *
 * Ported from the Slate-era webpack bundle (src/scripts/layout/theme.js and
 * its components). Each component is wrapped in an IIFE so nothing leaks
 * between them. Runs after DOM parse (defer), so no DOMContentLoaded wait is
 * needed for queries at the top level.
 *
 * Note on innerHTML: markup assigned below comes from Shopify's Section
 * Rendering API (same-origin, theme-rendered) and Vimeo's oembed API —
 * unchanged behavior from the original bundle.
 */

/* ============================================================
 * Accessibility helpers
 * Ported from @shopify/theme-a11y (focusHash, bindInPageLinks)
 * ============================================================ */
(function () {
  function forceFocus(element, options) {
    options = options || {};
    var savedTabIndex = element.tabIndex;
    element.tabIndex = -1;
    element.dataset.tabIndex = savedTabIndex;
    element.focus();
    if (typeof options.className !== "undefined") {
      element.classList.add(options.className);
    }
    element.addEventListener("blur", function callback(event) {
      event.target.removeEventListener(event.type, callback);
      element.tabIndex = savedTabIndex;
      delete element.dataset.tabIndex;
      if (typeof options.className !== "undefined") {
        element.classList.remove(options.className);
      }
    });
  }

  // Focus the element referenced by the URL hash on page load
  var hash = window.location.hash;
  var hashElement = hash && hash !== "#" ? document.getElementById(hash.slice(1)) : null;
  if (hashElement) {
    forceFocus(hashElement);
  }

  // Move focus to the target of in-page anchor links
  document.querySelectorAll('a[href^="#"]').forEach(function (link) {
    if (link.hash === "#" || link.hash === "") return;
    link.addEventListener("click", function () {
      var element = document.querySelector(link.hash);
      if (element) {
        forceFocus(element);
      }
    });
  });
})();

/* ============================================================
 * Cookie support class
 * ============================================================ */
if (navigator.cookieEnabled) {
  document.documentElement.className = document.documentElement.className.replace(
    "supports-no-cookies",
    "supports-cookies"
  );
}

/* ============================================================
 * Flat Tummy App webview detection (cart referral attribute)
 * ============================================================ */
(function () {
  function getCookie(name) {
    var match = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
    return match ? decodeURIComponent(match[1]) : undefined;
  }

  function setCookie(name, value) {
    document.cookie = name + "=" + encodeURIComponent(value) + "; path=/";
  }

  function setCartReferralAttribute(value) {
    fetch(window.Shopify.routes.root + "cart/update.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json"
      },
      body: JSON.stringify({
        attributes: {
          Referral: value
        }
      })
    });
  }

  var isFlatTummyApp = /FlatTummyApp/.test(navigator.userAgent);
  if (isFlatTummyApp) {
    setCartReferralAttribute("Flat Tummy App");
    setCookie("ft_app", "true");
  } else if (getCookie("ft_app") === "true") {
    setCartReferralAttribute(null);
    setCookie("ft_app", "false");
  }
})();

/* ============================================================
 * Images: lazy-load fade-in + Vimeo embeds
 * ============================================================ */
(function () {
  // Fade-in images
  if ("loading" in HTMLImageElement.prototype) {
    var images = document.querySelectorAll("img[loading=lazy]");

    var imageLoad = function (e) {
      e.currentTarget.classList.remove("image-loading");
    };

    var imageError = function (e) {
      var image = e.currentTarget;
      image.classList.remove("image-loading");
      image.classList.add("image-loading-error");
      setTimeout(function () {
        image.classList.add("image-loading-error-show");
      }, 60);
    };

    for (var image of images) {
      if (!image.complete) {
        image.classList.add("image-loading");
        image.addEventListener("load", imageLoad, false);
        image.addEventListener("error", imageError, false);
      }
    }
  }

  // Fetch Vimeo Data (to remove duplicate fetch URLs)
  function fetchVimeoData(videoId) {
    return fetch(
      `https://vimeo.com/api/oembed.json?url=https://vimeo.com/${videoId}&width=960&autoplay=1`
    )
      .then((response) => response.json())
      .catch((error) => {
        console.error(`🚨 Vimeo API Fetch Failed for Video ID ${videoId}:`, error);
        return null;
      });
  }

  // Play Vimeo video
  function playVimeo(videoId, videoElement, json, callback) {
    function scrollToVideo() {
      videoElement.scrollIntoView({ behavior: "smooth", block: "center" });
    }

    if (!json) {
      // If JSON is missing, fetch it and retry playing the video
      fetchVimeoData(videoId).then((jsonData) => {
        if (jsonData) playVimeo(videoId, videoElement, jsonData, callback);
      });
      return;
    }

    if (!videoElement.classList.contains("video-loaded")) {
      videoElement.innerHTML = json.html; // Load the iframe
      videoElement.classList.remove("video-ready");
      videoElement.classList.add("video-loaded");
    }

    const iframe = videoElement.querySelector("iframe");

    if (iframe) {
      scrollToVideo();
      iframe.contentWindow.postMessage('{"method":"play"}', "*");

      if (iframe.title) {
        const playVideoEvent = new CustomEvent("performance.selectContent", {
          detail: { content_type: "Play video", content_id: iframe.title }
        });
        window.dispatchEvent(playVideoEvent);
      } else {
        console.warn("⚠️ Skipping event dispatch—iframe title is not set yet.");
      }
    }

    if (typeof callback === "function") {
      callback(videoElement);
    }
  }

  // Load Vimeo video
  function loadVimeo(videoId, videoElement, callback = null) {
    fetchVimeoData(videoId).then((json) => {
      if (!json) return;

      const newImage = document.createElement("img");
      newImage.srcset =
        json.thumbnail_url.replace("_960", "_320") +
        " 320w," +
        json.thumbnail_url.replace("_960", "_480") +
        " 480w," +
        json.thumbnail_url.replace("_960", "_640") +
        " 640w," +
        json.thumbnail_url +
        " 960w";
      newImage.sizes = "(min-width: 992px) 960px, 70vw";
      newImage.alt = json.title;
      newImage.loading = "lazy";
      newImage.classList.add("embed-responsive-item");
      videoElement.appendChild(newImage);
      videoElement.classList.add("video-ready");

      newImage.addEventListener("click", function () {
        playVimeo(videoId, videoElement, json, callback);
      });
    });
  }

  // Load Vimeo videos dynamically
  document
    .querySelectorAll(".embed-responsive[data-load-video]:not(.video-loaded)")
    .forEach((video) => {
      const videoId = video.dataset.loadVideo;
      loadVimeo(videoId, video, typeof customAction !== "undefined" ? customAction : null);
    });

  // External Play Button Support
  document.querySelectorAll("[data-play-video]").forEach((button) => {
    button.addEventListener("click", (event) => {
      event.preventDefault();
      const videoId = button.getAttribute("data-play-video");
      const videoElement = document.querySelector(`[data-load-video="${videoId}"]`);

      if (videoElement) {
        playVimeo(videoId, videoElement, null, typeof customAction !== "undefined" ? customAction : null);
      } else {
        console.error(`❌ Video element not found for ID: ${videoId}`);
      }
    });
  });
})();

/* ============================================================
 * Search bar (header)
 * ============================================================ */
(function () {
  const searchBar = document.querySelectorAll("header .search-bar");
  const searchBarToggle = document.querySelectorAll("[data-search-bar-toggle]");

  function show(el) {
    document.documentElement.classList.add("backdrop-active");
    document.documentElement.classList.add("search-bar-active");
    if (el) {
      el.setAttribute("open", "");
      el.querySelector("input[type=search]").focus();
    }
  }

  function hide(el) {
    document.documentElement.classList.remove("backdrop-active");
    document.documentElement.classList.remove("search-bar-active");
    if (el) {
      el.removeAttribute("open");
      el.querySelector("input[type=search]").blur();
    }
  }

  searchBar.forEach((el) => {
    el.querySelector(".close__button").addEventListener("click", () => {
      hide(el);
    });
    el.addEventListener("keyup", (event) => {
      if (event.code.toUpperCase() === "ESCAPE") {
        hide(el);
      }
    });

    el.addEventListener("click", (event) => {
      if (event.target.hasAttribute("open")) {
        hide(el);
      }
    });

    searchBarToggle.forEach((toggle) => {
      toggle.addEventListener("click", (event) => {
        event.preventDefault();
        show(el);
      });
    });
  });
})();

/* ============================================================
 * Predictive search
 * ============================================================ */
(function () {
  const predictiveSearch = document.querySelectorAll("[data-search]");
  predictiveSearch.forEach((el) => {
    const searchInput = el.querySelector('input[type="search"]');
    const predictiveSearchResults = el.querySelector("[data-predictive-search]");
    let cachedResults = {};
    let statusElement;
    let loadingText;

    const form = el.querySelector("form.search");
    form.addEventListener("submit", onFormSubmit.bind(el));

    searchInput.addEventListener(
      "input",
      debounce((event) => {
        onChange(event);
      }, 300).bind(el)
    );
    searchInput.addEventListener("focus", onFocus.bind(el));
    el.addEventListener("focusout", onFocusOut.bind(el));
    el.addEventListener("keyup", onKeyup.bind(el));
    el.addEventListener("keydown", onKeydown.bind(el));

    function getQuery() {
      return searchInput.value.trim();
    }

    function onChange() {
      const searchTerm = getQuery();

      if (!searchTerm.length) {
        close(true);
        return;
      }

      getSearchResults(searchTerm);
    }

    function onFormSubmit(event) {
      if (!getQuery().length || el.querySelector('[aria-selected="true"] a')) {
        event.preventDefault();
      }

      const searchTerm = getQuery();
      const searchTermRedirects = window.searchTermRedirects;
      if (searchTermRedirects) {
        const searchTermRedirectMatchFound = searchTermRedirects.some((redirect) => {
          return redirect.searchTerms.indexOf(searchTerm.toLowerCase()) !== -1;
        });

        if (searchTermRedirectMatchFound) {
          searchTermRedirects.forEach((redirect) => {
            if (redirect.searchTerms.indexOf(searchTerm.toLowerCase()) !== -1) {
              event.preventDefault();
              const utm_parameters =
                "?utm_source=search-term-redirect&utm_medium=search-term&utm_content=" +
                searchTerm;
              window.location.href = window.location.origin + redirect.url + utm_parameters;
            }
          });
        }
      }
    }

    function onFocus() {
      const searchTerm = getQuery();

      if (!searchTerm.length) {
        return;
      }

      if (el.getAttribute("results") === "true") {
        open();
      } else {
        getSearchResults(searchTerm);
      }
    }

    function onFocusOut() {
      setTimeout(() => {
        if (!el.contains(document.activeElement)) {
          close();
        }
      });
    }

    function onKeyup(event) {
      if (!getQuery().length) {
        close(true);
      }
      event.preventDefault();

      switch (event.code) {
        case "ArrowUp":
          switchOption("up");
          break;
        case "ArrowDown":
          switchOption("down");
          break;
        case "Enter":
          selectOption();
          break;
      }
    }

    function onKeydown(event) {
      // Prevent the cursor from moving in the input when using the up and down arrow keys
      if (event.code === "ArrowUp" || event.code === "ArrowDown") {
        event.preventDefault();
      }
    }

    function switchOption(direction) {
      if (!el.getAttribute("open")) {
        return;
      }

      const moveUp = direction === "up";
      const selectedElement = el.querySelector('[aria-selected="true"]');
      const allElements = el.querySelectorAll("li");
      let activeElement = el.querySelector("li");

      if (moveUp && !selectedElement) {
        return;
      }

      statusElement.textContent = "";

      if (!moveUp && selectedElement) {
        activeElement = selectedElement.nextElementSibling || allElements[0];
      } else if (moveUp) {
        activeElement =
          selectedElement.previousElementSibling || allElements[allElements.length - 1];
      }

      if (activeElement === selectedElement) {
        return;
      }

      activeElement.setAttribute("aria-selected", true);
      if (selectedElement) {
        selectedElement.setAttribute("aria-selected", false);
      }

      setLiveRegionText(activeElement.textContent);
      searchInput.setAttribute("aria-activedescendant", activeElement.id);
    }

    function selectOption() {
      const selectedProduct = el.querySelector(
        '[aria-selected="true"] a, [aria-selected="true"] button'
      );

      if (selectedProduct) {
        selectedProduct.click();
      }
    }

    function getSearchResults(searchTerm) {
      const queryKey = searchTerm.replace(" ", "-").toLowerCase();
      setLiveRegionLoadingState();

      if (cachedResults[queryKey]) {
        renderSearchResults(cachedResults[queryKey]);
        return;
      }
      fetch(
        `${routes.predictive_search_url}?q=${encodeURIComponent(
          searchTerm
        )}&${encodeURIComponent("resources[type]")}=product&${encodeURIComponent(
          "resources[limit]"
        )}=4&section_id=predictive-search`
      )
        .then((response) => {
          if (!response.ok) {
            var error = new Error(response.status);
            close();
            throw error;
          }

          return response.text();
        })
        .then((text) => {
          const resultsMarkup = new DOMParser()
            .parseFromString(text, "text/html")
            .querySelector("#shopify-section-predictive-search").innerHTML;
          cachedResults[queryKey] = resultsMarkup;
          renderSearchResults(resultsMarkup);
        })
        .catch((error) => {
          close();
          throw error;
        });
    }

    function setLiveRegionLoadingState() {
      if (statusElement === undefined) {
        statusElement = el.querySelector(".predictive-search-status");
      }
      if (loadingText === undefined) {
        loadingText = el.getAttribute("data-loading-text");
      }
      setLiveRegionText(loadingText);
      el.setAttribute("loading", true);
    }

    function setLiveRegionText(statusText) {
      statusElement.setAttribute("aria-hidden", "false");
      statusElement.textContent = statusText;

      setTimeout(() => {
        statusElement.setAttribute("aria-hidden", "true");
      }, 1000);
    }

    function renderSearchResults(resultsMarkup) {
      predictiveSearchResults.innerHTML = resultsMarkup;
      el.setAttribute("results", true);

      setLiveRegionResults();
      open();
    }

    function setLiveRegionResults() {
      el.removeAttribute("loading");
      setLiveRegionText(
        el.querySelector("[data-predictive-search-live-region-count-value]").textContent
      );
    }

    function open() {
      el.setAttribute("open", true);
      searchInput.setAttribute("aria-expanded", true);
    }

    function close(clearSearchTerm = false) {
      if (clearSearchTerm) {
        searchInput.value = "";
        el.removeAttribute("results");
      }

      const selected = el.querySelector('[aria-selected="true"]');

      if (selected) {
        selected.setAttribute("aria-selected", false);
      }

      searchInput.setAttribute("aria-activedescendant", "");
      el.removeAttribute("open");
      searchInput.setAttribute("aria-expanded", false);
      predictiveSearchResults.removeAttribute("style");
    }

    function debounce(fn, wait) {
      let t;
      return (...args) => {
        clearTimeout(t);
        t = setTimeout(() => fn.apply(el, args), wait);
      };
    }
  });
})();

/* ============================================================
 * Collection/search facet filters
 * ============================================================ */
(function () {
  const FacetFiltersForm = document.querySelector("[data-facet-filters]");
  if (!FacetFiltersForm) return;

  function debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => {
        fn.apply(FacetFiltersForm, args), wait;
      });
    };
  }

  function onKeyUpEscape(event) {
    if (event.code.toUpperCase() !== "ESCAPE") return;

    const openDetailsElement = event.target.closest("details[open]");
    if (!openDetailsElement) return;

    const summaryElement = openDetailsElement.querySelector("summary");
    openDetailsElement.removeAttribute("open");
    summaryElement.setAttribute("aria-expanded", false);
    summaryElement.focus();
  }

  const facetWrapper = FacetFiltersForm.querySelector(".facets");
  if (facetWrapper) {
    facetWrapper.addEventListener("keyup", onKeyUpEscape);
  }

  function renderPage(searchParams, event, updateURL = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = getSections();
    const countContainer = document.getElementById("ProductCountContainer");
    const productList = document.getElementById("product-list");

    if (productList) {
      productList.classList.add("loading");
    }
    if (countContainer) {
      countContainer.classList.add("loading");
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => {
        element.url === url;
      };

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? renderSectionFromCache(filterDataUrl, event)
        : renderSectionFromFetch(url, event);
    });

    if (updateURL) {
      updateURLHash(searchParams);
    }
  }

  const renderProductsEvent = new CustomEvent("renderSection.product");
  function renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        renderFilters(html, event);
        renderProductGridContainer(html);
        renderProductCount(html);
        window.dispatchEvent(renderProductsEvent);
      });
  }

  function renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    renderFilters(html, event);
    renderProductGridContainer(html);
    renderProductCount(html);
    window.dispatchEvent(renderProductsEvent);
  }

  function renderProductGridContainer(html) {
    const productListContainer = document.getElementById("product-list");
    productListContainer.innerHTML = new DOMParser()
      .parseFromString(html, "text/html")
      .getElementById("product-list").innerHTML;
    productListContainer.classList.remove("loading");
  }

  function renderProductCount(html) {
    const countContent = new DOMParser()
      .parseFromString(html, "text/html")
      .getElementById("ProductCount").innerHTML;
    const count = document.getElementById("ProductCount");
    const countContainer = document.getElementById("ProductCountContainer");
    count.innerHTML = countContent;
    countContainer.classList.remove("loading");
  }

  function renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, "text/html");

    const facetDetailsElements = parsedHTML.querySelectorAll("#FacetFiltersForm .js-filter");
    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest(".js-filter") : undefined;
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    };
    const facetsToRender = Array.from(facetDetailsElements).filter(
      (element) => !matchesIndex(element)
    );
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      document.querySelector(`.js-filter[data-index="${element.dataset.index}"]`).innerHTML =
        element.innerHTML;
    });

    renderActiveFacets(parsedHTML);

    if (countsToRender) {
      renderCounts(countsToRender, event.target.closest(".js-filter"));
    }
  }

  function renderActiveFacets(html) {
    const activeFacetElementSelectors = [".active-facets"];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });
    addListnersFacetRemove();
  }

  function renderCounts(source, target) {
    const targetElement = target.querySelector(".facets__selected");
    const sourceElement = source.querySelector(".facets__selected");

    const targetElementAccessibility = target.querySelector(".facets__summary");
    const sourceElementAccessibility = source.querySelector(".facets__summary");

    if (sourceElement && targetElement) {
      target.querySelector(".facets__selected").outerHTML =
        source.querySelector(".facets__selected").outerHTML;
    }

    if (targetElementAccessibility && sourceElementAccessibility) {
      target.querySelector(".facets__summary").outerHTML =
        source.querySelector(".facets__summary").outerHTML;
    }
  }

  function updateURLHash(searchParams) {
    history.pushState(
      { searchParams },
      "",
      `${window.location.pathname}${searchParams && "?".concat(searchParams)}`
    );
  }

  function getSections() {
    return [
      {
        section: document.getElementById("facets").dataset.id
      }
    ];
  }

  function createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  function onSubmitForm(searchParams, event) {
    renderPage(searchParams, event);
  }

  function onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll("[data-facet-filters] form");

    const forms = [];

    sortFilterForms.forEach((form) => {
      forms.push(createSearchParams(form));
    });
    onSubmitForm(forms.join("&"), event);
  }

  function onActiveFilterClick(event) {
    event.preventDefault();
    const url =
      event.currentTarget.href.indexOf("?") == -1
        ? ""
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf("?") + 1);
    renderPage(url);
  }

  FacetFiltersForm.filterData = [];
  FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
  FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);

  function addListners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state
        ? event.state.searchParams
        : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) {
        return;
      }
      renderPage(searchParams, null, false);
    };
    window.addEventListener("popstate", onHistoryChange);

    const facetForm = document.querySelector("[data-facet-filters] form");
    facetForm.addEventListener(
      "input",
      debounce((event) => {
        onSubmitHandler(event);
      }, 500)
    );
    const facetFormOpen = facetForm.querySelector(".facets__heading");
    if (facetFormOpen) {
      facetFormOpen.addEventListener("click", () => {
        facetForm.classList.add("active");
      });
    }
    const facetFormClose = facetForm.querySelector(".facets__close");
    if (facetFormClose) {
      facetFormClose.addEventListener("click", () => {
        facetForm.classList.remove("active");
      });
    }
  }
  addListners();

  function addListnersFacetRemove() {
    const FacetRemove = document.querySelectorAll("[data-facet-remove]");
    FacetRemove.forEach((el) => {
      const facetLink = el.querySelector("a:not([role=button])");
      if (facetLink) {
        facetLink.setAttribute("role", "button");
        facetLink.addEventListener("click", closeFilter.bind(el));
        facetLink.addEventListener("keyup", (event) => {
          event.preventDefault();
          if (event.code.toUpperCase() === "SPACE") {
            closeFilter(event);
          }
        });
      }
    });
  }
  addListnersFacetRemove();

  function closeFilter(event) {
    event.preventDefault();
    onActiveFilterClick(event);
  }
})();

/* ============================================================
 * Zendesk live chat (lazy-loaded behind a button)
 * ============================================================ */
(function () {
  if (!window.theme || !window.theme.liveChat) return;

  const liveChatKeyStarted = "live_chat_started";
  const liveChatStarted = sessionStorage.getItem(liveChatKeyStarted);
  const livechatInit = function () {
    var script = document.createElement("script");
    script.defer = true;
    script.src =
      "https://static.zdassets.com/ekr/snippet.js?key=72a60653-1d66-4e74-8d2d-a99ce43afc18";
    script.id = "ze-snippet";
    document.head.appendChild(script);
    script.addEventListener("load", function () {
      window.zE("webWidget:on", "chat:start", function () {
        sessionStorage.setItem(liveChatKeyStarted, true);
      });
      window.zE("webWidget:on", "chat:end", function () {
        sessionStorage.removeItem(liveChatKeyStarted);
      });
      window.zE("webWidget", "open");
    });
    const liveChatLoadBtn = document.getElementById("liveChatButton");
    if (liveChatLoadBtn) {
      liveChatLoadBtn.remove();
    }
  };

  const livechatInitBtn = function () {
    const liveChatBtn = `<button id="liveChatButton" aria-label="Help" class="lc-btn"><span class="lc-icon"><svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px" width="20px" height="20px" viewBox="0 0 20 20" xml:space="preserve" aria-hidden="true"><path d="M11,12.3V13c0,0-1.8,0-2,0v-0.6c0-0.6,0.1-1.4,0.8-2.1c0.7-0.7,1.6-1.2,1.6-2.1c0-0.9-0.7-1.4-1.4-1.4 c-1.3,0-1.4,1.4-1.5,1.7H6.6C6.6,7.1,7.2,5,10,5c2.4,0,3.4,1.6,3.4,3C13.4,10.4,11,10.8,11,12.3z"></path><circle cx="10" cy="15" r="1"></circle><path d="M10,2c4.4,0,8,3.6,8,8s-3.6,8-8,8s-8-3.6-8-8S5.6,2,10,2 M10,0C4.5,0,0,4.5,0,10s4.5,10,10,10s10-4.5,10-10S15.5,0,10,0 L10,0z"></path></svg></span><span class="lc-label">Help</span></button><style type="text/css">.lc-btn {background-color:#f48b96; color:#69242b; fill:#69242b; border:0; margin:0; padding:1.4rem 2.2rem; font-size:1.6rem; border-radius:4.7rem; height:4.6rem; vertical-align:middle; box-shadow:none; -webkit-appearance:none; -moz-appearance:none; appearance:none; cursor:pointer; display:flex; align-items:center; position:fixed; bottom:1.4rem; right:2rem; z-index:1060; font-weight:700; font-family:-apple-system, BlinkMacSystemFont, Roboto, "Helvetica Neue", Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji";}.lc-label {padding-left:0.8rem;}.lc-icon svg {min-width:2rem; min-height:2rem; height:2rem; width:2rem; margin-top:0.2rem;}@media (max-width:575.98px) {.lc-btn {padding:1.4rem;} .lc-label {display:none;}}</style>`;
    document.body.insertAdjacentHTML("beforeend", liveChatBtn);
    document.getElementById("liveChatButton").addEventListener("click", livechatInit);
  };

  window.onload = function () {
    if (liveChatStarted) {
      livechatInit();
    } else {
      livechatInitBtn();
    }
  };
})();

/* ============================================================
 * Performance/analytics content-selection events
 * ============================================================ */
(function () {
  var detailsSummary = document.querySelectorAll(
    "details summary:not([data-select-content-id])"
  );
  if (detailsSummary) {
    detailsSummary.forEach(function (summary) {
      summary.addEventListener("click", function (event) {
        var toggleSummaryEvent = new CustomEvent("performance.selectContent", {
          detail: {
            content_type: "Toggle details",
            content_id: event.target.innerText
          }
        });
        window.dispatchEvent(toggleSummaryEvent);
      });
    });
  }

  const selectContentElements = document.querySelectorAll(
    "[data-select-content-id][data-select-content-type]"
  );

  const selectContent = function (event) {
    const selectContentEvent = new CustomEvent("performance.selectContent", {
      detail: {
        content_type: event.target.dataset.selectContentType,
        content_id: event.target.dataset.selectContentId
      }
    });
    window.dispatchEvent(selectContentEvent);
  };

  if (selectContentElements) {
    selectContentElements.forEach((selectContentElement) => {
      selectContentElement.addEventListener("click", (event) => {
        selectContent(event);
      });
    });
  }
})();
